/**
 * ApprovalDecisionModal — confirmation modal for Approve / Reject.
 *
 * - Approve: optional comment (encouraged for audit but not required).
 * - Reject : comment is required (rejection rationale required by compliance).
 * - On submit: POST to Graph (or simulated success when feature flag off),
 *   call onSuccess so the parent updates optimistic state, close modal.
 */

import { useEffect, useState } from 'react';
import { Button, ModalShell } from '@/components/primitives';
import { useApprovalDecision } from './hooks/useApprovalDecision';
import type { GraphApproval, ApprovalDecision } from './services/graphApprovalsClient';

export interface ApprovalDecisionModalProps {
  open: boolean;
  approval: GraphApproval | null;
  decision: ApprovalDecision | null;
  onClose: () => void;
  /** Called after a successful POST. Receives the decision so the parent can patch state. */
  onSuccess: (approval: GraphApproval, decision: ApprovalDecision, comment: string, isLive: boolean) => void;
}

export function ApprovalDecisionModal({
  open,
  approval,
  decision,
  onClose,
  onSuccess,
}: ApprovalDecisionModalProps): JSX.Element | null {
  const { submit, submitting, error } = useApprovalDecision();
  const [comment, setComment] = useState<string>('');
  const [touched, setTouched] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setComment('');
      setTouched(false);
    }
  }, [open, approval?.id, decision]);

  if (!approval || !decision) return null;

  const isReject = decision === 'reject';
  const commentRequired = isReject;
  const commentInvalid = commentRequired && touched && comment.trim().length === 0;
  const canSubmit = !submitting && (!commentRequired || comment.trim().length > 0);

  const handleSubmit = async (): Promise<void> => {
    setTouched(true);
    if (!approval.activeStageId) return;
    if (commentRequired && comment.trim().length === 0) return;
    const result = await submit(
      approval.id,
      approval.activeStageId,
      decision,
      comment.trim() || undefined,
    );
    if (result.ok) {
      onSuccess(approval, decision, comment.trim(), result.isLive);
      onClose();
    }
  };

  const title = isReject ? 'Reject this approval' : 'Approve this request';
  const submitLabel = isReject ? 'Reject' : 'Approve';
  const submitVariant: 'green' | 'danger' = isReject ? 'danger' : 'green';

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant={submitVariant}
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
          >
            {submitting ? 'Submitting…' : submitLabel}
          </Button>
        </>
      }
    >
      <div className="rap-decision-summary">
        <h3 className="rap-decision-summary__title">{approval.displayName}</h3>
        <div className="rap-decision-summary__row">
          <span className="rap-decision-summary__key">Requested by</span>
          <span>{approval.requesterDisplayName}</span>
        </div>
        {approval.customProperties['amount'] && (
          <div className="rap-decision-summary__row">
            <span className="rap-decision-summary__key">Amount</span>
            <span>{approval.customProperties['amount']}</span>
          </div>
        )}
        {approval.customProperties['customer'] && (
          <div className="rap-decision-summary__row">
            <span className="rap-decision-summary__key">Customer</span>
            <span>{approval.customProperties['customer']}</span>
          </div>
        )}
        {approval.customProperties['branch'] && (
          <div className="rap-decision-summary__row">
            <span className="rap-decision-summary__key">Branch</span>
            <span>{approval.customProperties['branch']}</span>
          </div>
        )}
      </div>

      <div>
        <label className="rap-comment-label" htmlFor="rap-decision-comment">
          {isReject ? 'Reason for rejection' : 'Comment'}{commentRequired ? ' *' : ' (optional)'}
        </label>
        <textarea
          id="rap-decision-comment"
          className="rap-comment-input"
          value={comment}
          placeholder={
            isReject
              ? 'Briefly explain why this is being rejected — visible to the requester.'
              : 'Optional note attached to your approval.'
          }
          onChange={(e) => {
            setComment(e.target.value);
            setTouched(true);
          }}
          onBlur={() => setTouched(true)}
        />
        {commentInvalid && (
          <span className="rap-comment-help" style={{ color: 'var(--err-text)' }}>
            A short rejection reason is required.
          </span>
        )}
        {!commentInvalid && (
          <span className="rap-comment-help">
            {isReject
              ? 'Visible to the requester and recorded in the audit trail.'
              : 'Recorded with the decision in the SessionEvents audit log.'}
          </span>
        )}
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-4) var(--sp-5)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            borderRadius: 'var(--r-sm)',
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
    </ModalShell>
  );
}
